import React, { useState, useMemo } from 'react';
import { ArrowRightLeft, Save, ClipboardList, CalendarCheck, AlertCircle, ChevronRight } from "lucide-react";

const DEFAULT_ITEMS = ["LPG 20kg", "LPG 50kg", "산소(O2)", "질소(N2)", "아르곤(Ar)"];

const WarehouseTransfer = ({ cylinders = [], setCylinders, warehouses = [], items = [], transfers = [], setTransfers }) => {
  const itemNames = items.length > 0 ? items.map(i => i.name) : DEFAULT_ITEMS;
  const today = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    date: today,
    fromWarehouse: warehouses[0] ?? '',
    toWarehouse: warehouses[1] ?? '',
    item: itemNames[0] ?? '',
    status: '실병',
    quantity: '',
    memo: '',
  });
  const [formError, setFormError] = useState('');
  const [saved, setSaved] = useState(false);

  const availableCount = useMemo(() =>
    cylinders.filter(c =>
      c.locType === 'warehouse' &&
      c.location === formData.fromWarehouse &&
      c.item === formData.item &&
      c.status === formData.status
    ).length,
    [cylinders, formData.fromWarehouse, formData.item, formData.status]
  );

  const toWarehouses = warehouses.filter(w => w !== formData.fromWarehouse);

  const handleFromChange = (val) => {
    const nextTo = warehouses.find(w => w !== val) ?? '';
    setFormData(prev => ({ ...prev, fromWarehouse: val, toWarehouse: nextTo }));
    setFormError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.fromWarehouse) { setFormError('출발 창고를 선택하세요.'); return; }
    if (!formData.toWarehouse)   { setFormError('도착 창고를 선택하세요.'); return; }
    if (formData.fromWarehouse === formData.toWarehouse) { setFormError('출발 창고와 도착 창고가 같습니다.'); return; }
    const qty = parseInt(formData.quantity) || 0;
    if (qty <= 0) { setFormError('이동 수량을 입력하세요.'); return; }
    if (qty > availableCount) {
      setFormError(`${formData.fromWarehouse}에 ${formData.item} ${formData.status}가 ${availableCount}개뿐입니다.`); return;
    }

    const toMove = cylinders
      .filter(c =>
        c.locType === 'warehouse' &&
        c.location === formData.fromWarehouse &&
        c.item === formData.item &&
        c.status === formData.status
      )
      .slice(0, qty)
      .map(c => c.id);

    setCylinders(prev => prev.map(c =>
      toMove.includes(c.id) ? { ...c, location: formData.toWarehouse } : c
    ));

    setTransfers(prev => [...prev, {
      id: Date.now(),
      date: formData.date,
      fromWarehouse: formData.fromWarehouse,
      toWarehouse: formData.toWarehouse,
      item: formData.item,
      status: formData.status,
      quantity: qty,
      memo: formData.memo,
      cylinders: toMove,
    }]);

    setFormData(prev => ({ ...prev, quantity: '', memo: '' }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* 등록 폼 */}
      <div style={sectionBox}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 25 }}>
          <ArrowRightLeft size={24} color="#d97706" />
          <h2 style={{ margin: 0, fontSize: 20, color: '#d97706' }}>창고 간 재고 이동</h2>
        </div>

        {warehouses.length < 2 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#aaa', fontSize: 14 }}>
            창고가 2개 이상 등록되어야 이동이 가능합니다.<br />
            <span style={{ fontSize: 12 }}>설정 → 창고 관리에서 창고를 추가하세요.</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* 창고 선택 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>출발 창고</label>
                <select style={inputStyle} value={formData.fromWarehouse}
                  onChange={e => handleFromChange(e.target.value)}>
                  {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div style={{ paddingTop: 26, color: '#d97706' }}>
                <ChevronRight size={28} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>도착 창고</label>
                <select style={inputStyle} value={formData.toWarehouse}
                  onChange={e => { setFormData(prev => ({ ...prev, toWarehouse: e.target.value })); setFormError(''); }}>
                  {toWarehouses.length === 0
                    ? <option value="">창고 없음</option>
                    : toWarehouses.map(w => <option key={w} value={w}>{w}</option>)
                  }
                </select>
              </div>
            </div>

            {/* 품목·상태·수량 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>이동 품목</label>
                <select style={inputStyle} value={formData.item}
                  onChange={e => { setFormData(prev => ({ ...prev, item: e.target.value })); setFormError(''); }}>
                  {itemNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>상태</label>
                <select style={inputStyle} value={formData.status}
                  onChange={e => { setFormData(prev => ({ ...prev, status: e.target.value })); setFormError(''); }}>
                  <option value="실병">실병</option>
                  <option value="공병">공병</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>
                  이동 수량 (본)
                  <span style={{ marginLeft: 8, fontWeight: 400, color: availableCount === 0 ? '#dc2626' : '#d97706', fontSize: 12 }}>
                    가용 {availableCount}개
                  </span>
                </label>
                <input type="number" min="1" max={availableCount} style={inputStyle}
                  placeholder="0"
                  value={formData.quantity}
                  onChange={e => { setFormData(prev => ({ ...prev, quantity: e.target.value })); setFormError(''); }} />
              </div>
            </div>

            {/* 날짜·메모 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>이동 일자</label>
                <input type="date" style={inputStyle} value={formData.date}
                  onChange={e => setFormData(prev => ({ ...prev, date: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>메모 (선택)</label>
                <input type="text" style={inputStyle} placeholder="이동 사유 등" value={formData.memo}
                  onChange={e => setFormData(prev => ({ ...prev, memo: e.target.value }))} />
              </div>
            </div>

            {/* 미리보기 */}
            {formData.quantity > 0 && !formError && (
              <div style={{ padding: '16px 20px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <ArrowRightLeft size={16} color="#d97706" />
                <span style={{ fontSize: 14, color: '#92400e' }}>
                  <strong>{formData.fromWarehouse}</strong> → <strong>{formData.toWarehouse}</strong>
                  &nbsp;&nbsp;{formData.item} {formData.status} <strong style={{ color: '#d97706' }}>{formData.quantity}본</strong> 이동
                </span>
              </div>
            )}

            {formError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                <AlertCircle size={15} color="#dc2626" />
                <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>{formError}</span>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button type="submit" style={{ ...submitBtn, flex: 1 }}>
                <Save size={18} /> 창고 이동 저장
              </button>
              {saved && <span style={{ fontSize: 13, color: '#d97706', fontWeight: 700, whiteSpace: 'nowrap' }}>✓ 이동 완료!</span>}
            </div>
          </form>
        )}
      </div>

      {/* 이동 이력 */}
      <div style={sectionBox}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <ClipboardList size={18} color="#d97706" />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>창고 이동 이력</h3>
          <span style={{ fontSize: 13, color: '#888' }}>총 {transfers.length}건</span>
        </div>
        {transfers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13, border: '1px dashed #e5e3db', borderRadius: 10 }}>
            등록된 이동 이력이 없습니다
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...transfers].reverse().map(t => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a', flexWrap: 'wrap' }}>
                <CalendarCheck size={14} color="#d97706" />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#333', minWidth: 90 }}>{t.date}</span>
                <span style={{ fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <strong>{t.fromWarehouse}</strong>
                  <ChevronRight size={14} />
                  <strong>{t.toWarehouse}</strong>
                </span>
                <span style={{ fontSize: 12, background: '#fef3c7', color: '#92400e', padding: '2px 10px', borderRadius: 10, fontWeight: 600 }}>
                  {t.item} {t.status} {t.quantity}본
                </span>
                {t.memo && (
                  <span style={{ fontSize: 12, color: '#777', flex: 1 }}>{t.memo}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 8 };
const inputStyle  = { width: '100%', padding: '12px', borderRadius: 8, border: '1px solid #d3d1c7', fontSize: 15, boxSizing: 'border-box', background: '#fcfbf7' };
const submitBtn   = { padding: '15px', background: '#d97706', color: 'white', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 };
const sectionBox  = { background: 'white', padding: 30, borderRadius: 16, border: '1px solid #d3d1c7' };

export default WarehouseTransfer;
